import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  setPersistence,
  browserLocalPersistence,
  getAdditionalUserInfo,
  type UserCredential,
} from "firebase/auth";
import { auth, googleProvider, facebookProvider, appleProvider, db } from "./config";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { refreshCurrentAuthUser, subscribeToAuthState } from "./auth-state";
import { createApiUrl, isSpaFallthrough } from "../utils/api/apiClient";
import { resolveLocalDevApiUrl } from "../utils/api/localDevApiUrl";
import { getFirebaseAuthErrorMessage } from "../utils/firebaseAuthErrorMessage";
import { shouldQueueEmailVerification, shouldQueueWelcomeEmail } from "../utils/auth/emailVerification";
import { markPendingEmailConfirmation, markSignupAwaitingConfirmation, clearPendingEmailConfirmation } from "../utils/navigation/pendingEmailConfirmation";
import { trackEvent } from "../utils/analytics/trackEvent";

/** Count a new account (social sign-ins only create one the first time). */
const trackIfNewAccount = (result: UserCredential, method: string) => {
  if (getAdditionalUserInfo(result)?.isNewUser) {
    trackEvent("signup_completed", { method });
  }
};

const isLocalDevelopmentHost = () => {
  if (typeof window === "undefined") return false;

  const { hostname } = window.location;
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]" ||
    hostname.endsWith(".local")
  );
};

const shouldPreferGoogleRedirect = () => !isLocalDevelopmentHost();

import {
  hasPendingAuthRedirectFlag,
  hasRedirectResultFlag,
  REDIRECT_PENDING_KEY,
  REDIRECT_UID_KEY,
} from "./redirectFlags";

const setRedirectPendingFlag = () => {
  try {
    sessionStorage.setItem(REDIRECT_PENDING_KEY, "1");
  } catch {
    // ignore
  }
};

const clearRedirectPendingFlag = () => {
  try {
    sessionStorage.removeItem(REDIRECT_PENDING_KEY);
  } catch {
    // ignore
  }
};

const shouldFallbackToOAuthRedirect = (error: unknown) => {
  const code =
    typeof error === "object" && error && "code" in error
      ? String((error as { code?: string }).code)
      : "";
  return code === "auth/popup-blocked";
};

const queueUserDocumentSync = (user: User) => {
  // Do not block auth UX on Firestore writes.
  createOrUpdateUserDocument(user).catch((error) => {
    console.error("❌ Error syncing user document:", error);
  });
};

const sendVerificationEmailToUser = async (user: User) => {
  const idToken = await user.getIdToken();
  const endpoint = "/api/emails/verify";
  const response = await fetch(createApiUrl(endpoint), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      displayName: user.displayName || undefined,
    }),
  });

  if (isSpaFallthrough(endpoint, response)) {
    throw new Error(
      "Verification email request failed: the API route did not resolve.",
    );
  }

  const payload = (await response.json().catch(() => null)) as
    | { error?: string; skipped?: boolean; reason?: string; sent?: boolean }
    | null;

  if (response.ok) {
    return;
  }

  const message =
    (payload && typeof payload.error === "string" && payload.error) ||
    `Verification email request failed with status ${response.status}`;

  if (response.status === 429) {
    const tooMany = new Error(message);
    (tooMany as { code?: string }).code = "auth/too-many-requests";
    throw tooMany;
  }

  throw new Error(message);
};

export type VerificationSendOutcome =
  | { status: "sent" }
  | { status: "not-needed" }
  | { status: "failed"; error: string };

/**
 * Latest send started for a uid. The request is fired at signup so it does not
 * depend on the browser staying on any particular screen, but `/verify-email`
 * still needs to report whether it worked — a refused send used to surface only
 * as a console error, leaving the member waiting for mail that never comes.
 */
let latestVerificationSend:
  | { uid: string; outcome: Promise<VerificationSendOutcome> }
  | null = null;

const startEmailVerification = (
  user: User,
): Promise<VerificationSendOutcome> => {
  if (!shouldQueueEmailVerification(user)) {
    return Promise.resolve({ status: "not-needed" });
  }

  markPendingEmailConfirmation(user.uid);

  const outcome = sendVerificationEmailToUser(user).then(
    () => ({ status: "sent" }) as VerificationSendOutcome,
    (error: unknown) => {
      console.error("❌ Error sending email verification:", error);
      return {
        status: "failed",
        error: getFirebaseAuthErrorMessage(
          error ?? "Failed to send verification email.",
        ),
      } as VerificationSendOutcome;
    },
  );

  latestVerificationSend = { uid: user.uid, outcome };
  return outcome;
};

/** Outcome of the send started at signup, or null if none was started for this uid. */
export const getStartedVerificationSend = (
  uid: string,
): Promise<VerificationSendOutcome> | null => {
  return latestVerificationSend?.uid === uid
    ? latestVerificationSend.outcome
    : null;
};

export const requestEmailVerification = async (user?: User | null) => {
  const current = user ?? auth?.currentUser ?? null;
  if (!current) {
    return {
      success: false,
      error: "Sign in to verify your email.",
    };
  }

  if (current.emailVerified) {
    return {
      success: true,
      message: "Your email is already verified.",
    };
  }

  try {
    await sendVerificationEmailToUser(current);
    return {
      success: true,
      message: "Verification email sent. Check your inbox and spam folder.",
    };
  } catch (error: unknown) {
    console.error("🔴 Email verification error:", error);

    if (
      typeof error === "object" &&
      error !== null &&
      (error as { code?: string }).code === "auth/too-many-requests"
    ) {
      return {
        success: false,
        error: "Too many verification emails. Wait a bit, then try again.",
      };
    }

    return {
      success: false,
      error: getFirebaseAuthErrorMessage(
        error ?? "Failed to send verification email.",
      ),
    };
  }
};

const queueWelcomeEmail = async (user: User) => {
  if (typeof window === "undefined") return;
  if (!shouldQueueWelcomeEmail(user)) return;
  const email = user.email;
  if (!email) return;

  try {
    const idToken = await user.getIdToken(true);
    const url = createApiUrl("/api/emails/welcome");

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        uid: user.uid,
        email,
        displayName: user.displayName || undefined,
      }),
    });

    const payload = (await response.json().catch(() => null)) as
      | { error?: string; skipped?: boolean; reason?: string }
      | null;

    if (response.ok) {
      if (payload?.skipped) {
        console.info(
          `[welcome-email] Skipped for ${user.uid}${payload.reason ? `: ${payload.reason}` : ""}`,
        );
      }
      return;
    }

    const message =
      (payload && typeof payload.error === "string" && payload.error) ||
      `Welcome email request failed with status ${response.status}`;
    console.warn(`[welcome-email] ${message}`);
  } catch (error) {
    console.warn(
      "[welcome-email] Request failed (sign-in is unaffected):",
      error instanceof Error ? error.message : error,
    );
  }
};


// Subscribe to authentication state changes (no-op when auth is unavailable)
export const subscribeToAuthChanges = subscribeToAuthState;

export const refreshAuthenticatedUser = refreshCurrentAuthUser;

// Exported so re-exports in src/firebase/auth.ts can name it. With
// composite: true tsc checks declaration emit, and a type it cannot name
// is an error (TS4023) even under --noEmit.
export interface GoogleSignInResult {
  success: boolean;
  redirected?: boolean;
  error?: string;
}

/** Same shape as Google — popup or redirect OAuth. */
type FacebookSignInResult = GoogleSignInResult;

// Sign in with Google.
export const signInWithGoogle = async (): Promise<GoogleSignInResult> => {
  if (!auth || !googleProvider) {
    return {
      success: false,
      error: "Authentication is not available in this environment.",
    };
  }

  try {
    await setPersistence(auth, browserLocalPersistence);

    if (shouldPreferGoogleRedirect()) {
      setRedirectPendingFlag();
      await signInWithRedirect(auth, googleProvider);
      return { success: true, redirected: true };
    }

    const result = await signInWithPopup(auth, googleProvider);
    if (result.user) {
      trackIfNewAccount(result, "google");
      queueUserDocumentSync(result.user);
    }
    return { success: true, redirected: false };
  } catch (error: any) {
    if (shouldFallbackToOAuthRedirect(error) && !isLocalDevelopmentHost()) {
      try {
        setRedirectPendingFlag();
        await signInWithRedirect(auth, googleProvider);
        return { success: true, redirected: true };
      } catch (redirectError: any) {
        clearRedirectPendingFlag();
        return {
          success: false,
          error: getFirebaseAuthErrorMessage(
            redirectError ?? "Google redirect sign-in failed",
          ),
        };
      }
    }

    clearRedirectPendingFlag();

    return {
      success: false,
      error: getFirebaseAuthErrorMessage(error ?? "Google sign-in failed"),
    };
  }
};

// Sign in with Facebook (Firebase Auth → Facebook OAuth).
export const signInWithFacebook = async (): Promise<FacebookSignInResult> => {
  if (!auth || !facebookProvider) {
    return {
      success: false,
      error: "Authentication is not available in this environment.",
    };
  }

  try {
    await setPersistence(auth, browserLocalPersistence);

    if (shouldPreferGoogleRedirect()) {
      setRedirectPendingFlag();
      await signInWithRedirect(auth, facebookProvider);
      return { success: true, redirected: true };
    }

    const result = await signInWithPopup(auth, facebookProvider);
    if (result.user) {
      trackIfNewAccount(result, "facebook");
      queueUserDocumentSync(result.user);
    }
    return { success: true, redirected: false };
  } catch (error: unknown) {
    if (shouldFallbackToOAuthRedirect(error) && !isLocalDevelopmentHost()) {
      try {
        setRedirectPendingFlag();
        await signInWithRedirect(auth, facebookProvider);
        return { success: true, redirected: true };
      } catch (redirectError: unknown) {
        clearRedirectPendingFlag();
        return {
          success: false,
          error: getFirebaseAuthErrorMessage(
            redirectError ?? "Facebook redirect sign-in failed",
          ),
        };
      }
    }

    clearRedirectPendingFlag();

    return {
      success: false,
      error: getFirebaseAuthErrorMessage(error ?? "Facebook sign-in failed"),
    };
  }
};

/** Same shape as Google — popup or redirect OAuth. */
type AppleSignInResult = GoogleSignInResult;

export const signInWithApple = async (): Promise<AppleSignInResult> => {
  if (!auth || !appleProvider) {
    return {
      success: false,
      error: "Authentication is not available in this environment.",
    };
  }

  try {
    await setPersistence(auth, browserLocalPersistence);

    if (shouldPreferGoogleRedirect()) {
      setRedirectPendingFlag();
      await signInWithRedirect(auth, appleProvider);
      return { success: true, redirected: true };
    }

    const result = await signInWithPopup(auth, appleProvider);
    if (result.user) {
      trackIfNewAccount(result, "apple");
      queueUserDocumentSync(result.user);
    }
    return { success: true, redirected: false };
  } catch (error: unknown) {
    if (shouldFallbackToOAuthRedirect(error) && !isLocalDevelopmentHost()) {
      try {
        setRedirectPendingFlag();
        await signInWithRedirect(auth, appleProvider);
        return { success: true, redirected: true };
      } catch (redirectError: unknown) {
        clearRedirectPendingFlag();
        return {
          success: false,
          error: getFirebaseAuthErrorMessage(
            redirectError ?? "Apple redirect sign-in failed",
          ),
        };
      }
    }

    clearRedirectPendingFlag();

    return {
      success: false,
      error: getFirebaseAuthErrorMessage(error ?? "Apple sign-in failed"),
    };
  }
};

// getRedirectResult() can only be called once per redirect. Start it at module load so we run before any React effect and win any race.
let redirectResultPromise: Promise<User | null> | null = null;
if (typeof window !== "undefined" && auth) {
  redirectResultPromise = (async () => {
    try {
      const result = await getRedirectResult(auth!);
      if (result?.user) {
        try {
          sessionStorage.setItem(REDIRECT_UID_KEY, result.user.uid);
        } catch {
          // ignore
        }
        // providerId is "google.com"; popups record "google", so match them.
        trackIfNewAccount(result, (result.providerId ?? "redirect").replace(/\.com$/, ""));
        queueUserDocumentSync(result.user);
        return result.user;
      }
    } catch (error: unknown) {
      console.error("🔴 Redirect sign-in error:", error);
    } finally {
      clearRedirectPendingFlag();
    }
    return null;
  })();
}

/** Same check as `hasPendingAuthRedirectFlag` in ./redirectFlags; kept under
 *  this name for existing callers, but delegating so there is one implementation. */
export function hasPendingRedirectFlag(): boolean {
  return hasPendingAuthRedirectFlag();
}

/**
 * Clears only the redirect UID marker.
 *
 * Deliberately narrower than `clearRedirectResultFlag` in ./redirectFlags,
 * which also clears the pending flag. Here the pending flag has its own
 * lifecycle - `clearRedirectPendingFlag` runs in the module-load redirect
 * handler above - so clearing it here would be premature. Named for what it
 * actually does, since the two used to share a name and silently differ.
 */
export function clearRedirectUidFlag(): void {
  try {
    sessionStorage.removeItem(REDIRECT_UID_KEY);
  } catch {
    // ignore
  }
}

/** Re-exported from ./redirectFlags — the two copies were byte-identical. */
export { hasRedirectResultFlag };

export const handleRedirectResult = (): Promise<User | null> => {
  return redirectResultPromise ?? Promise.resolve(null);
};

// Sign up with email and password
export const signUpWithEmail = async (
  email: string,
  password: string,
  displayName: string,
) => {
  try {
    if (!auth) {
      throw new Error("Authentication is not available in this environment.");
    }
    markSignupAwaitingConfirmation();
    const result = await createUserWithEmailAndPassword(auth, email, password);
    clearRedirectPendingFlag();

    // Update the user's display name
    await updateProfile(result.user, {
      displayName: displayName,
    });

    // Fire-and-forget profile sync so signup completes quickly. The verification
    // send starts here too, but its outcome is handed back so the caller can route
    // to /verify-email and report a failed send instead of hiding it.
    let verification: Promise<VerificationSendOutcome> = Promise.resolve({
      status: "not-needed",
    });
    if (result.user) {
      trackEvent("signup_completed", { method: "email" });
      queueUserDocumentSync(result.user);
      verification = startEmailVerification(result.user);
    }

    return { success: true, user: result.user, verification };
  } catch (error: unknown) {
    clearPendingEmailConfirmation();
    console.error("🔴 Sign-up error:", error);
    return {
      success: false,
      error: getFirebaseAuthErrorMessage(error),
    };
  }
};

// Sign in with email and password
export const signInWithEmail = async (email: string, password: string) => {
  try {
    if (!auth) {
      throw new Error("Authentication is not available in this environment.");
    }
    const result = await signInWithEmailAndPassword(auth, email, password);
    clearRedirectPendingFlag();

    // Fire-and-forget profile sync so sign-in completes quickly.
    if (result.user) {
      queueUserDocumentSync(result.user);
    }

    return { success: true, user: result.user };
  } catch (error: unknown) {
    console.error("🔴 Sign-in error:", error);
    return {
      success: false,
      error: getFirebaseAuthErrorMessage(error),
    };
  }
};

export const requestPasswordReset = async (email: string) => {
  const trimmedEmail = email.trim();
  const successMessage =
    "If an account exists for that email, a password reset link has been sent.";

  if (!trimmedEmail) {
    return {
      success: false,
      error: "Enter your email address first.",
    };
  }

  try {
    if (!auth) {
      throw new Error("Authentication is not available in this environment.");
    }

    await sendPasswordResetEmail(auth, trimmedEmail);
    return {
      success: true,
      message: successMessage,
    };
  } catch (error: unknown) {
    console.error("🔴 Password reset error:", error);

    if (
      typeof error === "object" &&
      error !== null &&
      (error as { code?: string }).code === "auth/user-not-found"
    ) {
      return {
        success: true,
        message: successMessage,
      };
    }

    if (
      typeof error === "object" &&
      error !== null &&
      (error as { code?: string }).code === "auth/invalid-email"
    ) {
      return {
        success: false,
        error: "Enter a valid email address.",
      };
    }

    if (
      typeof error === "object" &&
      error !== null &&
      (error as { code?: string }).code === "auth/too-many-requests"
    ) {
      return {
        success: false,
        error: "Too many reset attempts. Please wait a bit and try again.",
      };
    }

    if (
      typeof error === "object" &&
      error !== null &&
      (error as { code?: string }).code === "auth/operation-not-allowed"
    ) {
      return {
        success: false,
        error: "Password reset is not enabled for this project.",
      };
    }

    return {
      success: false,
      error: getFirebaseAuthErrorMessage(
        error ?? "Failed to send password reset email.",
      ),
    };
  }
};


// Create or update user document in Firestore
const createOrUpdateUserDocument = async (user: User) => {
  if (!db) {
    console.warn("Firestore unavailable — skipped user profile sync.");
    return;
  }

  try {
    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      const userData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        createdAt: new Date(),
        updatedAt: new Date(),
        isPremiumUser: false,
        isAnnualMember: false,
        preferences: {
          homeState: null,
          hasCompletedOnboarding: false,
        },
      };

      await setDoc(userRef, userData);
    } else {
      await setDoc(
        userRef,
        {
          displayName: user.displayName ?? null,
          photoURL: user.photoURL ?? null,
          lastSignInAt: new Date(),
          updatedAt: new Date(),
        },
        { merge: true },
      );
    }
  } catch (error) {
    console.error("❌ Error creating/updating user document:", error);
    return;
  }

  // Welcome waits until emailVerified. Password signups skip here; the confirm
  // link and a later sign-in retry it. Server also no-ops if already sent.
  void queueWelcomeEmail(user);
};

type AccountDeletionBlockerLink = {
  label: string;
  path: string;
};

export type AccountDeletionBlocker = {
  code: string;
  message: string;
  bookingId?: string;
  hostEventId?: string;
  bookingStatus?: string;
  links?: AccountDeletionBlockerLink[];
};

function parseAccountDeletionBlockers(
  raw: unknown,
): AccountDeletionBlocker[] | undefined {
  if (!Array.isArray(raw)) {
    return undefined;
  }

  const blockers: AccountDeletionBlocker[] = [];

  for (const item of raw) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const o = item as Record<string, unknown>;
    const code = typeof o.code === "string" ? o.code : "";
    const message = typeof o.message === "string" ? o.message : "";
    if (!code || !message) {
      continue;
    }

    const linksRaw = o.links;
    let links: AccountDeletionBlockerLink[] | undefined;
    if (Array.isArray(linksRaw)) {
      links = linksRaw
        .map((L) => {
          if (!L || typeof L !== "object") {
            return null;
          }
          const link = L as Record<string, unknown>;
          const label = typeof link.label === "string" ? link.label : "";
          const path = typeof link.path === "string" ? link.path : "";
          if (!label || !path) {
            return null;
          }
          return { label, path };
        })
        .filter((x): x is AccountDeletionBlockerLink => x !== null);
    }

    blockers.push({
      code,
      message,
      bookingId:
        typeof o.bookingId === "string" ? o.bookingId : undefined,
      hostEventId:
        typeof o.hostEventId === "string" ? o.hostEventId : undefined,
      bookingStatus:
        typeof o.bookingStatus === "string" ? o.bookingStatus : undefined,
      links: links && links.length > 0 ? links : undefined,
    });
  }

  return blockers.length > 0 ? blockers : undefined;
}

const ACCOUNT_DELETION_BLOCKED_SUMMARY_FALLBACK =
  "You can't delete your account until hosting and payment obligations are cleared. Use the automated actions in account settings when offered, then try again.";

/**
 * GET /api/delete-user-data — returns blockers before the user attempts deletion (same rules as POST).
 */
export const fetchAccountDeletionBlockers = async (): Promise<{
  canDelete: boolean;
  blockers?: AccountDeletionBlocker[];
  summary?: string;
  error?: string;
}> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { canDelete: false, error: 'No authenticated user found' };
    }

    const idToken = await user.getIdToken();
    const response = await fetch(resolveLocalDevApiUrl("/api/delete-user-data"), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });

    const payload = (await response.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;

    if (!response.ok) {
      // Server often puts details on `message` (e.g. Firestore index / stack) and a generic `error`.
      const detail =
        (typeof payload.message === 'string' && payload.message) ||
        (typeof payload.error === 'string' && payload.error) ||
        '';
      const message =
        detail.trim() ||
        `Could not check account deletion status (HTTP ${response.status})`;
      return { canDelete: false, error: message };
    }

    const blockers = parseAccountDeletionBlockers(payload.blockers);
    const canDelete = payload.canDelete === true;
    const summary =
      (typeof payload.message === 'string' && payload.message) ||
      (blockers && blockers.length > 0
        ? ACCOUNT_DELETION_BLOCKED_SUMMARY_FALLBACK
        : undefined);

    return {
      canDelete,
      blockers: blockers && blockers.length > 0 ? blockers : undefined,
      summary: blockers && blockers.length > 0 ? summary : undefined,
    };
  } catch (networkError) {
    console.error('Account deletion status request failed:', networkError);
    return {
      canDelete: false,
      error:
        'We could not check whether your account can be deleted. Check your connection and try again.',
    };
  }
};

/** Deletes all user data via POST /api/delete-user-data (server-side only; enforces marketplace rules). */
export const deleteAllUserData = async (options?: {
  /** Optional exit survey code (logged server-side only). */
  deletionReasonCode?: string;
}): Promise<
  | { success: true; message: string }
  | {
      success: false;
      error: string;
      code?: string;
      blockers?: AccountDeletionBlocker[];
    }
> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return {
        success: false,
        error: "No authenticated user found",
      };
    }

    const idToken = await user.getIdToken();
    let payload: Record<string, unknown> = {};
    try {
      const body: Record<string, string> = {};
      const code = options?.deletionReasonCode?.trim();
      if (code) {
        body.deletionReasonCode = code;
      }
      const response = await fetch(resolveLocalDevApiUrl("/api/delete-user-data"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      payload = (await response.json().catch(() => ({}))) as Record<
        string,
        unknown
      >;

      if (!response.ok) {
        const code =
          typeof payload.code === "string" ? payload.code : undefined;
        const blockers = parseAccountDeletionBlockers(payload.blockers);
        const message =
          (typeof payload.message === "string" && payload.message) ||
          (typeof payload.error === "string" && payload.error) ||
          "Failed to delete user data";
        return {
          success: false,
          error: message,
          code,
          blockers,
        };
      }
    } catch (networkError) {
      console.error("❌ Account deletion request failed:", networkError);
      return {
        success: false,
        error:
          "We could not reach the server to delete your account. Check your connection and try again. Account deletion must complete on our servers for your data and event bookings to be handled safely.",
      };
    }

    await auth.signOut();

    return {
      success: true,
      message:
        (typeof payload.message === "string" && payload.message) ||
        "All user data has been successfully deleted",
    };
  } catch (error: unknown) {
    console.error("❌ Error deleting user data:", error);
    const message =
      error instanceof Error ? error.message : "Failed to delete user data";
    return {
      success: false,
      error: message,
    };
  }
};
